import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  resolvePositionMarkPrice,
  symbolsMatch,
} from "@helpers/formatOrders";
import { buildPositionLines } from "./buildPositionLines";

const getPositionSymbol = (position) =>
  position?.symbol ?? position?.pair ?? position?.pair_name ?? position?.slug;

const NO_POSITIONS = [];

const selectPositionLinesState = ({ futures }) => ({
  // Persisted futures state does not keep open positions
  openPositions: Array.isArray(futures.openPositions)
    ? futures.openPositions
    : NO_POSITIONS,
  positionMarkPrices: futures.positionMarkPrices,
  markPrice: futures.markPrice,
  price: futures.price,
  chartPrice: futures.chartPrice,
  lastPrice: futures.tickerData?.lastPrice,
  tickersMap: futures.tickersMap,
  positionLevelsVisible: futures.positionLevelsVisible !== false,
});

/**
 * Chart models of the open positions of a pair with the live PnL, the texts
 * of the lines and the "P&L lines" toggle. The chart engine draws them.
 *
 * @param {string} symbol pair slug, e.g. "BTCUSDT"
 * @returns {{
 *   models: import("./buildPositionLines").PositionLinesModel[],
 *   labels: { entry: string, takeProfit: string, stopLoss: string, liquidation: string, close: string },
 *   isVisible: boolean,
 * }}
 */
export const useOpenPositionModels = (symbol) => {
  // useTranslation returns a new function each render, intl is stable per locale
  const intl = useIntl();
  const {
    openPositions,
    positionMarkPrices,
    markPrice,
    price,
    chartPrice,
    lastPrice,
    tickersMap,
    positionLevelsVisible,
  } = useSelector(selectPositionLinesState, shallowEqual);

  const labels = useMemo(() => {
    const t = (id) => intl.formatMessage({ id });

    return {
      entry: t("futures.chart.entry"),
      takeProfit: t("futures.tp_sl.take_profit"),
      stopLoss: t("futures.tp_sl.stop_loss"),
      liquidation: t("futures.liquidation"),
      close: t("futures.close"),
    };
  }, [intl]);

  const models = useMemo(
    () =>
      openPositions
        .filter(
          (position) =>
            !position.isGuideMock &&
            symbolsMatch(getPositionSymbol(position), symbol),
        )
        .map((position) =>
          buildPositionLines(
            position,
            resolvePositionMarkPrice(position, {
              markPrices: positionMarkPrices,
              pairSlug: symbol,
              activePairMarkPrice: markPrice,
              lastPrice: price ?? lastPrice ?? chartPrice,
              tickersMap,
            }),
          ),
        )
        .filter(Boolean),
    [
      openPositions,
      symbol,
      positionMarkPrices,
      markPrice,
      price,
      lastPrice,
      chartPrice,
      tickersMap,
    ],
  );

  return { models, labels, isVisible: positionLevelsVisible };
};
